import React, { useEffect, useState } from "react";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Building2 } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({ name: "", place: "", kind_attention: "", contact: "", details: "", trn: "" });

const CustomersPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/customers");
      setItems(data);
    } catch {
      toast.error("Failed to load customers");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...blank(), ...c }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await apiClient.post("/customers", form);
        toast.success("Customer saved");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((c) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      c.name?.toLowerCase().includes(t) ||
      c.place?.toLowerCase().includes(t) ||
      c.kind_attention?.toLowerCase().includes(t) ||
      c.contact?.toLowerCase().includes(t)
    );
  });

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold ep-text-primary">Customers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Saved customers auto-fill their details when you build a quote — no more retyping.
          </p>
        </div>
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={openCreate} data-testid="add-customer-button">
          <Plus className="mr-2 h-4 w-4" /> Add Customer
        </Button>
      </div>

      <Card className="rounded-sm border-[#E5E7EB] shadow-none">
        <div className="border-b border-[#E5E7EB] p-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, place, contact…"
              className="rounded-sm pl-9"
              data-testid="customer-search-input"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Place</th>
                <th className="px-4 py-3">Kind Attention</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    {items.length === 0
                      ? "No customers yet. They are saved automatically when you save a quote, or add one here."
                      : "No customers match your search."}
                  </td>
                </tr>
              ) : filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E7EB] hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium ep-text-primary">
                    {c.name}
                    {c.trn && <div className="text-[11px] font-normal text-slate-400">TRN: {c.trn}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{c.place || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.kind_attention || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.contact || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" data-testid={`delete-customer-${c.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone. Existing quotes are not affected.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(c.id)} className="rounded-sm bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#1E40AF]" />
              {editing ? `Edit Customer — ${editing.name}` : "Add Customer"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Customer / Company Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-sm" data-testid="customer-form-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Place / City</Label>
              <Input value={form.place} onChange={(e) => set("place", e.target.value)} placeholder="Dubai, UAE" className="mt-1 rounded-sm" data-testid="customer-form-place" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Kind Attention</Label>
              <Input value={form.kind_attention} onChange={(e) => set("kind_attention", e.target.value)} placeholder="Mr. Paul George" className="mt-1 rounded-sm" data-testid="customer-form-attention" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Contact Number</Label>
              <Input value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="+971-50-…" className="mt-1 rounded-sm" data-testid="customer-form-contact" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">TRN (Tax Registration No.)</Label>
              <Input value={form.trn} onChange={(e) => set("trn", e.target.value)} placeholder="100xxxxxxxxxxxx" className="mt-1 rounded-sm" data-testid="customer-form-trn" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Address / Details</Label>
              <Textarea value={form.details} onChange={(e) => set("details", e.target.value)} rows={2} placeholder="Full address, PO Box, country…" className="mt-1 rounded-sm" data-testid="customer-form-details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="rounded-sm ep-amber hover:ep-amber" data-testid="save-customer-form-button">
              {saving ? "Saving…" : editing ? "Save Changes" : "Save Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
